import { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowRight, MoveRight } from "lucide-react";
import { modesData, longevityCopy, media } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

const fanLayout = [
  { x: "0%", y: 12, rotate: -7 },
  { x: "12%", y: 2, rotate: -3.5 },
  { x: "24%", y: 16, rotate: 0.5 },
  { x: "36%", y: 6, rotate: 4.5 },
  { x: "48%", y: 18, rotate: 8 },
];

export const LongevitySection = () => {
  const stackRef = useRef(null);
  const [count, setCount] = useState(0);
  const complete = count >= modesData.length;

  const { scrollYProgress } = useScroll({
    target: stackRef,
    offset: ["start 0.95", "start 0.4"],
  });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const next = Math.min(modesData.length, Math.floor(v * (modesData.length + 0.4)));
    setCount((prev) => (next > prev ? next : prev));
  });

  return (
    <section
      data-testid="longevity-section"
      className="bg-[var(--strollee-accent)] px-6 lg:px-10 py-16 lg:py-20 text-[var(--strollee-surface)]"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[560px]">
          <p
            data-testid="longevity-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[#B9D0C0]"
          >
            {longevityCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em]">
            {longevityCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-white/70">
          {longevityCopy.support}
        </p>
      </motion.div>

      <div className="mt-12 lg:mt-14 grid grid-cols-[1fr_auto_1.15fr] items-center gap-8 lg:gap-12">
        <div>
          <p
            data-testid="longevity-old-label"
            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50"
          >
            {longevityCopy.oldWayLabel}
          </p>
          <div ref={stackRef} data-testid="old-way-stack" className="relative mt-5 h-[280px] lg:h-[320px]">
            {modesData.map((mode, i) => (
              <motion.div
                key={mode.id}
                initial={false}
                animate={
                  i < count
                    ? { opacity: 1, x: 0, y: fanLayout[i].y, rotate: fanLayout[i].rotate }
                    : { opacity: 0, x: -56, y: fanLayout[i].y, rotate: fanLayout[i].rotate - 8 }
                }
                transition={{ duration: 0.6, ease: EASE }}
                style={{ left: fanLayout[i].x }}
                className="absolute top-0 w-[44%] overflow-hidden rounded-2xl border border-white/15 shadow-[0_12px_32px_rgba(0,0,0,0.25)]"
              >
                <img
                  src={mode.image.url}
                  alt={mode.image.alt}
                  style={mode.image.position ? { objectPosition: mode.image.position } : undefined}
                  className="aspect-[4/3] w-full object-cover grayscale-[60%] opacity-75"
                  draggable={false}
                />
              </motion.div>
            ))}
          </div>
          <motion.p
            initial={false}
            animate={{ opacity: complete ? 1 : 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-5 text-sm font-medium text-white/60"
          >
            {longevityCopy.oldWayCaption}
          </motion.p>
        </div>

        <motion.div
          initial={false}
          animate={{ opacity: complete ? 1 : 0, scale: complete ? 1 : 0.7 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-white/10"
          aria-hidden="true"
        >
          <MoveRight size={18} strokeWidth={2} />
        </motion.div>

        <div>
          <p
            data-testid="longevity-new-label"
            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#B9D0C0]"
          >
            {longevityCopy.newWayLabel}
          </p>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: EASE }}
            data-testid="new-way-frame"
            className="mt-5 overflow-hidden rounded-[var(--strollee-radius-hero)] border-2 border-white/30 shadow-[0_28px_64px_rgba(0,0,0,0.35)]"
          >
            <img
              src={media.heroMedia.url}
              alt="One Strollee S1 system"
              className="h-[340px] lg:h-[400px] w-full object-cover"
              draggable={false}
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-5 text-base font-semibold text-white"
          >
            {longevityCopy.newWayCaption}
          </motion.p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
        className="mt-12 lg:mt-14 flex items-center justify-between gap-8 border-t border-white/15 pt-7"
      >
        <div className="flex items-center gap-7 text-[13px] font-medium text-white/70">
          {longevityCopy.benefits.map((b, i) => (
            <span key={b} data-testid={`longevity-benefit-${i}`} className="flex items-center gap-7">
              {i > 0 && <span className="h-1 w-1 rounded-full bg-white/35" />}
              {b}
            </span>
          ))}
        </div>
        <a
          href="#modes"
          data-testid="longevity-cta"
          className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold tracking-wide text-white transition-colors duration-200 hover:text-[#B9D0C0]"
        >
          {longevityCopy.cta}
          <ArrowRight
            size={16}
            strokeWidth={2.2}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </a>
      </motion.div>
    </section>
  );
};
